/**
 * The Auger side of calendar triage: an event record for a meeting, a note on
 * it, and mirroring the meeting's resource into Pipedrive. Google Calendar
 * stays with each interface; Pipedrive arrives as a client the caller passes.
 */

import { declaredSchemaFieldKeys } from "../collections/records.js"
import type { CreatedRecord, createRecordsService, FullRecord } from "../services/records.js"
import { type createSourcesService, SourceConflictError, sourceHolder } from "../services/sources.js"

type Records = ReturnType<typeof createRecordsService>
type Sources = ReturnType<typeof createSourcesService>

/** The Pipedrive creates a mirror needs, and the deletes that undo one.
 *  `@auger/pipedrive`'s client fits it. */
export interface PipedriveCreates {
  organizations: {
    create(params: { name: string }): Promise<{ id: number }>
    delete(id: number): Promise<unknown>
  }
  people: {
    create(params: { name: string }): Promise<{ id: number }>
    delete(id: number): Promise<unknown>
  }
  deals: {
    create(params: { title: string; org_id: number }): Promise<{ id: number }>
    delete(id: number): Promise<unknown>
  }
}

export interface CreateEventInput {
  schemaId: number
  name: string
  startAt?: string | null
  endAt?: string | null
  timezone?: string | null
  /** The Google Calendar event — the bridge triage reconciles against. */
  gcalEventId?: string | null
  /** The Pipedrive activity already logged for the meeting. */
  pipedriveActivityId?: number | string | null
  /** Link field key → the records it links to (resource, participants). */
  links?: Record<string, number[]>
  /** The series the event is an edition of. */
  parentId?: number | null
}

export interface CreatedEvent {
  /** The new record, or the record that already held the gcal source. */
  record: CreatedRecord | FullRecord
  /** The links written, by key. */
  links: Record<string, number[]>
  /** Link keys left out because the event's schemas don't declare them — a row
   *  under such a key is one no read would ever see. */
  undeclared: string[]
  /** True when an event already held the Google Calendar event, so that event
   *  came back and nothing new was created. */
  existing?: boolean
}

/** A record to mirror, with its sources and, for a deal, its organization. */
export interface MirrorSubject {
  id: number
  type: string
  name: string
  sources?: Array<{ source_type: string; source_id: string }> | null
  org?: MirrorSubject | null
}

export interface Mirrored {
  pipedriveId: number
  /** False when the record already had its Pipedrive source. */
  created: boolean
}

/** The source linking an Auger record to its Pipedrive mirror, by record type. */
export const PIPEDRIVE_SOURCE_TYPES = {
  organization: "pipedrive_organization",
  person: "pipedrive_person",
  deal: "pipedrive_deal",
} as const

export const pipedriveSourceId = (subject: MirrorSubject, sourceType: string): number | null => {
  const hit = (subject.sources || []).find((s) => s.source_type === sourceType)
  return hit ? Number(hit.source_id) : null
}

export function createCalendarService({
  records,
  sources,
}: {
  records: Records
  sources: Sources
}) {
  /** One record, with its time, its sources and its links, in one transaction:
   *  a failed link leaves no half-made event behind. */
  async function createEvent(input: CreateEventInput): Promise<CreatedEvent> {
    const declared = declaredSchemaFieldKeys({ type: "event", schema_id: input.schemaId })
    const links: Record<string, number[]> = {}
    const undeclared: string[] = []
    for (const [key, ids] of Object.entries(input.links ?? {})) {
      if (!ids.length) continue
      if (declared.has(key)) links[key] = [...new Set(ids)]
      else undeclared.push(key)
    }

    const data: Record<string, unknown> = { ...links }
    if (input.startAt) {
      data.times = [
        {
          timeType: "event",
          startAt: input.startAt,
          endAt: input.endAt ?? undefined,
          title: input.name,
          timezone: input.timezone ?? undefined,
        },
      ]
    }
    const eventSources = []
    if (input.gcalEventId)
      eventSources.push({ sourceType: "gcal_event", sourceId: input.gcalEventId })
    if (input.pipedriveActivityId) {
      eventSources.push({
        sourceType: "pipedrive_event",
        sourceId: String(input.pipedriveActivityId),
      })
    }
    if (eventSources.length) data.sources = eventSources

    // Triage again after a category or resource change clears the event id
    // in Google Calendar, but the gcal_event source stays on the record. Find
    // the record that holds the source first and return it, so the calendar
    // and Auger agree again, and no create is sent that the source trigger
    // would refuse.
    if (input.gcalEventId) {
      const held = await findEvent(input.gcalEventId)
      if (held) return reuseEvent(held, input, undeclared)
    }

    try {
      const record = await records.create({
        type: "event",
        name: input.name,
        schema_id: input.schemaId,
        ...(input.parentId ? { parent_id: input.parentId } : {}),
        data,
      })
      return { record, links, undeclared }
    } catch (err) {
      // The lookup can miss a holder: one made after it ran, or one you cannot
      // see. The source trigger then refuses the create and names the holder.
      const holder = input.gcalEventId ? sourceHolder(err, "gcal_event", input.gcalEventId) : undefined
      if (holder === undefined) throw err
      const record = await records.get(holder)
      if (!reusable(record)) throw err
      return reuseEvent(record, input, undeclared)
    }
  }

  // An archived event still holds its source, but it is not one to reuse.
  const reusable = (record: FullRecord | null | undefined): record is FullRecord =>
    record?.type === "event" && record.archived_at == null

  /** The live event record that holds a Google Calendar event's gcal_event
   *  source, or null when there is none. */
  async function findEvent(gcalEventId: string): Promise<FullRecord | null> {
    const id = await sources.holder({
      sourceType: "gcal_event",
      sourceId: gcalEventId,
      recordType: "event",
    })
    if (id == null) return null
    const record = await records.get(id)
    return reusable(record) ? record : null
  }

  async function reuseEvent(
    record: FullRecord,
    input: CreateEventInput,
    undeclared: string[]
  ): Promise<CreatedEvent> {
    const held = record.sources ?? []
    if (input.pipedriveActivityId && !held.some((s) => s.source_type === "pipedrive_event")) {
      try {
        await sources.add({
          recordId: record.id,
          sourceType: "pipedrive_event",
          sourceId: String(input.pipedriveActivityId),
        })
      } catch (err) {
        if (!(err instanceof SourceConflictError)) throw err
      }
    }
    return { record, links: {}, undeclared, existing: true }
  }

  /** A note document, related to its event record when there is one, in one
   *  transaction. */
  async function createNote(input: {
    schemaId: number
    eventRecordId?: number | null
    content?: string | null
    title?: string | null
  }) {
    const data: Record<string, unknown> = { content: input.content ?? "" }
    if (input.eventRecordId) data.related_to = [input.eventRecordId]
    return records.create({
      type: "document",
      name: input.title || "Note",
      schema_id: input.schemaId,
      data,
    })
  }

  /** Mirror an organization, person or deal into Pipedrive and link the two
   *  with a source. A record already linked is left as it is; a deal mirrors
   *  its organization first, since Pipedrive deals need one. */
  async function mirrorToPipedrive(
    subject: MirrorSubject,
    pipedrive: PipedriveCreates
  ): Promise<Mirrored> {
    const sourceType = PIPEDRIVE_SOURCE_TYPES[subject.type as keyof typeof PIPEDRIVE_SOURCE_TYPES]
    if (!sourceType) throw new Error(`A ${subject.type} record has no Pipedrive counterpart`)
    const existing = pipedriveSourceId(subject, sourceType)
    if (existing != null) return { pipedriveId: existing, created: false }

    let pipedriveId: number
    let kind: "deals" | "people" | "organizations"
    if (subject.type === "deal") {
      if (!subject.org) throw new Error("Deal has no linked organization")
      const org = await mirrorToPipedrive({ ...subject.org, type: "organization" }, pipedrive)
      kind = "deals"
      pipedriveId = (await pipedrive.deals.create({ title: subject.name, org_id: org.pipedriveId }))
        .id
    } else if (subject.type === "person") {
      kind = "people"
      pipedriveId = (await pipedrive.people.create({ name: subject.name })).id
    } else {
      kind = "organizations"
      pipedriveId = (await pipedrive.organizations.create({ name: subject.name })).id
    }
    try {
      await sources.add({ recordId: subject.id, sourceType, sourceId: String(pipedriveId) })
    } catch (err) {
      // Without its source, nothing in Auger points at the new Pipedrive
      // object, so a retry would create a second one. Delete it, so a retry
      // starts clean, and name it when even the delete fails.
      try {
        await pipedrive[kind].delete(pipedriveId)
      } catch {
        throw new Error(
          `Pipedrive ${subject.type} ${pipedriveId} was created, but linking it to record ` +
            `${subject.id} and deleting it both failed. Delete it in Pipedrive before a retry.`,
          { cause: err }
        )
      }
      throw err
    }
    return { pipedriveId, created: true }
  }

  return { createEvent, findEvent, createNote, mirrorToPipedrive }
}
